// Share modal — shown after a guest finishes uploading. Turns every contributor
// into a distributor (the smart enhancement from PRD).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Copy, Check, Mail, MoreHorizontal, X as XIcon, ArrowRight } from "lucide-react";

function WhatsAppIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
      <path d="M20.5 3.5A11.5 11.5 0 0 0 3.7 17.4L2.5 22l4.7-1.2A11.5 11.5 0 1 0 20.5 3.5Zm-8.5 18a9.5 9.5 0 0 1-4.8-1.3l-.3-.2-2.8.7.8-2.7-.2-.3a9.5 9.5 0 1 1 7.3 3.8Zm5.5-7.1c-.3-.2-1.8-.9-2-1s-.5-.2-.7.2-.8 1-.9 1.1-.3.2-.6 0a7.7 7.7 0 0 1-3.8-3.3c-.3-.5.3-.5.8-1.5a.5.5 0 0 0 0-.5c0-.2-.7-1.6-.9-2.1s-.5-.5-.7-.5h-.6a1 1 0 0 0-.8.3 3.2 3.2 0 0 0-1 2.4c0 1.4 1 2.7 1.2 2.9s2 3 4.9 4.2a16 16 0 0 0 1.6.6 4 4 0 0 0 1.8.1 3 3 0 0 0 2-1.4 2.4 2.4 0 0 0 .2-1.4c-.1-.1-.3-.2-.6-.3Z"/>
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 3V6a1 1 0 0 1 1-1Z" stroke="#22E2FF" strokeWidth="1.7" strokeLinejoin="round"/>
      <circle cx="9" cy="11.5" r="1" fill="#22E2FF"/>
      <circle cx="12.5" cy="11.5" r="1" fill="#22E2FF"/>
      <circle cx="16" cy="11.5" r="1" fill="#22E2FF"/>
    </svg>
  );
}

function ShareCircle({ children, onClick, bg, border, testid, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
      data-testid={testid}
      title={label}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center transition group-hover:-translate-y-0.5"
        style={{ background: bg, border: `1px solid ${border}` }}
      >
        {children}
      </div>
      <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.65)" }}>{label}</span>
    </button>
  );
}

export default function ShareAlbumModal({
  open,
  onClose,
  onView,
  albumName,
  slug,
  uploadedCount,
  uploaderName,
}) {
  const url = useMemo(() => `${window.location.origin}/${slug}`, [slug]);
  const message = useMemo(() => {
    const who = uploaderName ? `${uploaderName} just` : "I just";
    const what =
      uploadedCount === 1
        ? "added a photo"
        : `added ${uploadedCount} photos & videos`;
    return `${who} ${what} to "${albumName}" on mEYEcam — add yours: ${url}`;
  }, [albumName, slug, uploadedCount, uploaderName, url]);
  const [copied, setCopied] = useState(false);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Lock scroll behind the modal
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const share = (target) => {
    const enc = encodeURIComponent(message);
    const targets = {
      whatsapp: `https://wa.me/?text=${enc}`,
      sms: `sms:?&body=${enc}`,
      email: `mailto:?subject=${encodeURIComponent(`Photos from ${albumName}`)}&body=${enc}`,
    };
    if (targets[target]) window.open(targets[target], "_blank", "noopener");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Album link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — long-press the link instead");
    }
  };

  const tryNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: albumName, text: message, url });
        return;
      } catch { /* user cancelled — no toast */ }
    }
    copy();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="share-album-modal"
    >
      <div
        className="glass-card p-5 sm:p-6 w-full max-w-md relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 30px 80px -20px rgba(127,75,255,0.45), 0 24px 60px -28px rgba(34,226,255,0.55)",
        }}
      >
        {/* Decorative aura */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -right-20 w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(34,226,255,0.22), transparent 70%)" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 -left-20 w-64 h-64 rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(127,75,255,0.25), transparent 70%)" }}
        />

        <button
          ref={closeBtnRef}
          onClick={onClose}
          className="absolute top-3 right-3 rounded-full p-2 transition hover:bg-white/10"
          aria-label="Close"
          data-testid="share-modal-close"
        >
          <XIcon size={16} color="rgba(255,255,255,0.75)" />
        </button>

        <div className="relative">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
            style={{
              background: "linear-gradient(90deg, rgba(127,75,255,0.18), rgba(34,226,255,0.18))",
              border: "1px solid rgba(127,75,255,0.45)",
            }}
          >
            <Sparkles size={12} color="#22E2FF" />
            <span className="text-[11px] font-semibold tracking-wide" style={{ color: "#C9B6FF" }}>
              UPLOADED!
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
            {uploadedCount === 1 ? "Your photo is in." : `${uploadedCount} memories added.`}
          </h2>
          <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.7)" }}>
            Tell the rest of the crew so they can drop theirs too — the album&nbsp;
            <span className="font-medium" style={{ color: "#fff" }}>{albumName}</span> grows every time someone shares.
          </p>

          {/* URL pill */}
          <button
            type="button"
            onClick={copy}
            className="w-full mt-4 flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition hover:bg-white/5"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
            data-testid="share-modal-copy-pill"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>
                Album link
              </div>
              <div className="text-sm truncate font-medium" style={{ color: "#fff" }}>{url}</div>
            </div>
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold shrink-0"
              style={{
                background: copied ? "rgba(34,226,255,0.18)" : "rgba(127,75,255,0.18)",
                border: `1px solid ${copied ? "#22E2FF" : "rgba(127,75,255,0.55)"}`,
                color: copied ? "#22E2FF" : "#C9B6FF",
              }}
            >
              {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
            </span>
          </button>

          {/* Share targets */}
          <div className="grid grid-cols-4 gap-2 mt-5">
            <ShareCircle
              bg="#25D36622" border="#25D36666"
              onClick={() => share("whatsapp")}
              testid="share-modal-whatsapp" label="WhatsApp"
            >
              <WhatsAppIcon />
            </ShareCircle>
            <ShareCircle
              bg="#22E2FF22" border="#22E2FF66"
              onClick={() => share("sms")}
              testid="share-modal-sms" label="Messages"
            >
              <SmsIcon />
            </ShareCircle>
            <ShareCircle
              bg="#7F4BFF22" border="#7F4BFF66"
              onClick={() => share("email")}
              testid="share-modal-email" label="Email"
            >
              <Mail size={18} color="#C9B6FF" />
            </ShareCircle>
            <ShareCircle
              bg="rgba(255,255,255,0.05)" border="rgba(255,255,255,0.18)"
              onClick={tryNative}
              testid="share-modal-more" label="More"
            >
              <MoreHorizontal size={18} color="#fff" />
            </ShareCircle>
          </div>

          {/* CTAs */}
          <button
            onClick={onView}
            className="cta-pill w-full mt-6"
            data-testid="share-modal-view-gallery"
          >
            <span>See the gallery</span>
            <ArrowRight size={16} />
          </button>
          <button
            onClick={onClose}
            className="w-full mt-2 text-xs py-2 rounded-full transition hover:bg-white/5"
            style={{ color: "rgba(255,255,255,0.55)" }}
            data-testid="share-modal-upload-more"
          >
            Upload more first
          </button>
        </div>
      </div>
    </div>
  );
}
